import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Warning, Bug } from "@phosphor-icons/react";

/**
 * ErrorAlertBanner — shown on the Admin overview, pulls /admin/errors/recent.
 * Red banner when count_1h > 0, amber when count_24h > 0 but count_1h == 0,
 * hidden when both are zero.
 */
export default function ErrorAlertBanner() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/admin/errors/recent", { params: { hours: 24 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  if ((data.count_24h || 0) === 0) return null;

  const hot = (data.count_1h || 0) > 0;
  const bgCls = hot
    ? "bg-destructive/10 border-destructive/40 text-destructive"
    : "bg-amber-50 border-amber-300 text-amber-900";

  return (
    <div className={`border rounded-sm px-4 py-3 mb-6 ${bgCls}`} data-testid="admin-error-banner">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Warning size={18} weight="fill" />
          {hot
            ? `${data.count_1h} erreur${data.count_1h > 1 ? "s" : ""} 5xx dans la dernière heure`
            : `${data.count_24h} erreur${data.count_24h > 1 ? "s" : ""} 5xx dans les 24 dernières heures`}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-semibold underline opacity-80 hover:opacity-100"
          data-testid="admin-error-banner-toggle"
        >
          {open ? "Masquer" : "Voir détails"}
        </button>
      </div>

      {open && (
        <div className="mt-3 bg-white/70 border border-border rounded-sm divide-y divide-border max-h-80 overflow-y-auto">
          {(data.items || []).slice(0, 20).map((it, i) => (
            <div key={it.id || `${it.at}-${it.path}-${i}`} className="px-3 py-2 text-xs flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" data-testid={`admin-error-row-${i}`}>
              <span className="font-mono text-muted-foreground shrink-0">{new Date(it.at).toLocaleString("fr-FR")}</span>
              <span className="font-bold text-destructive">{it.status}</span>
              <span className="font-mono">{it.method} {it.path}</span>
              {it.exc && (
                <span className="truncate text-destructive/80" title={it.exc}>
                  <Bug size={10} className="inline" /> {it.exc.slice(0, 60)}
                </span>
              )}
            </div>
          ))}
          {(data.items || []).length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">Aucun détail disponible</div>
          )}
        </div>
      )}
    </div>
  );
}
